import {
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type HTMLAttributes,
    type ReactNode,
} from 'react';
import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type CollisionDetection,
    type DragEndEvent,
    type DragStartEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    ArrowDown,
    BarChart3,
    ChevronDown,
    ClipboardList,
    Folder,
    Globe,
    GripVertical,
    Megaphone,
    Monitor,
    PanelLeftClose,
    PanelLeftOpen,
    QrCode,
    Search,
    Star,
    User,
    Users,
    type LucideIcon,
} from 'lucide-react';

import { Input } from 'components/ui/input';
import { Separator } from 'components/ui/separator';
import Tooltip from 'components/popups/Tooltip';
import { cn } from 'lib/utils';

import { ReportLibraryItem } from './ReportLibraryItem';
import type { ReportView } from './api/reportViewTypes';
import { FAVORITES_SECTION_ID, type ReportDefinition, type ReportSection } from './mock/reports';
import {
    getStoredExpandedSectionIds,
    setStoredExpandedSectionIds,
} from './reportLibraryCollapsedSectionsStorage';

type ReportLibraryProps = {
    sections: ReportSection[];
    selectedReportId: string;
    selectedViewId?: number;
    myViews: ReportView[];
    sharedViews: ReportView[];
    onSelectReport: (reportId: string) => void;
    onSelectView: (view: ReportView) => void;
    onToggleFavorite: (reportId: string) => void;
    onReorderSection: (sectionId: string, orderedIds: string[]) => void;
    onReorderSections: (orderedSectionIds: string[]) => void;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
};

const collapseButtonClass =
    'cursor-pointer rounded-md border border-foreground/20 bg-muted p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground';

const SIDEBAR_TRANSITION_MS = 300;

const MY_VIEWS_SECTION_ID = 'my-views';
const SHARED_VIEWS_SECTION_ID = 'shared-views';

const SECTION_HEADER_ICONS: Record<string, LucideIcon> = {
    [FAVORITES_SECTION_ID]: Star,
    [MY_VIEWS_SECTION_ID]: User,
    [SHARED_VIEWS_SECTION_ID]: Users,
    online: Globe,
    pos: Monitor,
    'ticket-scanning': QrCode,
    marketing: Megaphone,
    'attendee-data': ClipboardList,
    'event-totals': BarChart3,
};

function ReportLibrarySectionIcon({ sectionId }: { sectionId: string }) {
    const Icon = SECTION_HEADER_ICONS[sectionId] ?? Folder;
    return <Icon aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />;
}

function ReportLibraryCountBadge({ count }: { count: number }) {
    return (
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold tabular-nums text-muted-foreground">
            {count}
        </span>
    );
}

type ReportLibraryAccordionProps = {
    sectionId: string;
    title: string;
    count: number;
    isOpen: boolean;
    onToggle: () => void;
    children: ReactNode;
    dragHandleProps?: HTMLAttributes<HTMLButtonElement>;
    isSectionDragging?: boolean;
};

function ReportLibraryAccordion({
    sectionId,
    title,
    count,
    isOpen,
    onToggle,
    children,
    dragHandleProps,
    isSectionDragging = false,
}: ReportLibraryAccordionProps) {
    const canReorderSection = Boolean(dragHandleProps);

    return (
        <div data-testid={`report-library-section-${sectionId}`}>
            <h3 className="group m-0 p-0">
                <div className="flex items-center gap-0.5">
                    <button
                        disabled={!canReorderSection}
                        type="button"
                        className={cn(
                            '-ml-1 shrink-0 touch-none appearance-none border-0 bg-transparent p-0 text-muted-foreground shadow-none',
                            'opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100',
                            isSectionDragging && 'opacity-100',
                        )}
                        style={
                            canReorderSection
                                ? { cursor: isSectionDragging ? 'grabbing' : 'grab' }
                                : { cursor: 'not-allowed' }
                        }
                        aria-label={`Drag ${title} section`}
                        {...dragHandleProps}
                    >
                        <GripVertical
                            className={cn('h-4 w-4', !canReorderSection && 'opacity-50')}
                        />
                    </button>
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
                        className="reports-library-section-toggle flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 appearance-none border-0 bg-transparent p-0 text-left shadow-none"
                    >
                        <span className="flex min-w-0 items-center gap-2">
                            <ReportLibrarySectionIcon sectionId={sectionId} />
                            <span className="truncate text-sm font-bold text-foreground">
                                {title}
                            </span>
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5">
                            <ReportLibraryCountBadge count={count} />
                            <ChevronDown
                                aria-hidden
                                className={cn(
                                    'reports-library-section-chevron h-4 w-4 text-muted-foreground',
                                    !isOpen && 'is-collapsed',
                                )}
                            />
                        </span>
                    </button>
                </div>
            </h3>
            <div className={cn('reports-library-section-body', isOpen && 'is-open')}>
                <div
                    className={cn(
                        'reports-library-section-body-inner',
                        !isOpen && 'pointer-events-none',
                    )}
                    aria-hidden={!isOpen}
                >
                    <div className="mt-2 flex flex-col">{children}</div>
                </div>
            </div>
        </div>
    );
}

function getSortableReportId(sectionId: string, reportId: string) {
    return `report:${sectionId}:${reportId}`;
}

function getSortableSectionId(sectionId: string) {
    return `section:${sectionId}`;
}

const closestSameType: CollisionDetection = (args) => {
    const activeType = args.active.data.current?.type;
    const activeSectionId = args.active.data.current?.sectionId;

    return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((container) => {
            const data = container.data.current;
            if (!data || data.type !== activeType) {
                return false;
            }

            if (activeType === 'report') {
                return data.sectionId === activeSectionId;
            }

            return data.sectionId !== FAVORITES_SECTION_ID;
        }),
    });
};

function ReportLibrarySectionOverlay({
    sectionId,
    title,
    count,
}: {
    sectionId: string;
    title: string;
    count: number;
}) {
    return (
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-1.5 shadow-md ring-2 ring-primary/20">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            <ReportLibrarySectionIcon sectionId={sectionId} />
            <span className="text-sm font-bold text-foreground">{title}</span>
            <ReportLibraryCountBadge count={count} />
        </div>
    );
}

type SortableReportLibraryItemProps = {
    sectionId: string;
    report: ReportDefinition;
    isActive: boolean;
    showCategoryBadge: boolean;
    onSelect: (reportId: string) => void;
    onToggleFavorite: (reportId: string) => void;
};

function SortableReportLibraryItem({
    sectionId,
    report,
    isActive,
    showCategoryBadge,
    onSelect,
    onToggleFavorite,
}: SortableReportLibraryItemProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: getSortableReportId(sectionId, report.id),
        data: { type: 'report', sectionId, reportId: report.id },
    });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition: isDragging ? undefined : transition,
                opacity: isDragging ? 0 : undefined,
            }}
        >
            <ReportLibraryItem
                report={report}
                isActive={isActive}
                showCategoryBadge={showCategoryBadge}
                onSelect={onSelect}
                onToggleFavorite={onToggleFavorite}
                dragHandleProps={{ ...attributes, ...listeners }}
            />
        </div>
    );
}

type ReportSectionItemsProps = {
    section: ReportSection;
    selectedReportId: string;
    selectedViewId?: number;
    onSelectReport: (reportId: string) => void;
    onToggleFavorite: (reportId: string) => void;
    canReorder: boolean;
};

function ReportSectionItems({
    section,
    selectedReportId,
    selectedViewId,
    onSelectReport,
    onToggleFavorite,
    canReorder,
}: ReportSectionItemsProps) {
    const sortableIds = section.reports.map((report) => getSortableReportId(section.id, report.id));

    if (!canReorder) {
        return (
            <>
                {section.reports.map((report) => (
                    <ReportLibraryItem
                        key={`${section.id}-${report.id}`}
                        report={report}
                        isActive={!selectedViewId && selectedReportId === report.id}
                        showCategoryBadge={section.id === FAVORITES_SECTION_ID}
                        onSelect={onSelectReport}
                        onToggleFavorite={onToggleFavorite}
                    />
                ))}
            </>
        );
    }

    return (
        <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col">
                {section.reports.map((report) => (
                    <SortableReportLibraryItem
                        key={`${section.id}-${report.id}`}
                        sectionId={section.id}
                        report={report}
                        isActive={!selectedViewId && selectedReportId === report.id}
                        showCategoryBadge={section.id === FAVORITES_SECTION_ID}
                        onSelect={onSelectReport}
                        onToggleFavorite={onToggleFavorite}
                    />
                ))}
            </div>
        </SortableContext>
    );
}

type SortableLibrarySectionProps = {
    section: ReportSection;
    selectedReportId: string;
    selectedViewId?: number;
    onSelectReport: (reportId: string) => void;
    onToggleFavorite: (reportId: string) => void;
    canReorder: boolean;
    isOpen: boolean;
    onToggle: () => void;
};

function SortableLibrarySection({
    section,
    selectedReportId,
    selectedViewId,
    onSelectReport,
    onToggleFavorite,
    canReorder,
    isOpen,
    onToggle,
}: SortableLibrarySectionProps) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: getSortableSectionId(section.id),
        data: { type: 'section', sectionId: section.id },
        disabled: !canReorder,
    });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition: isDragging ? undefined : transition,
                opacity: isDragging ? 0 : undefined,
            }}
        >
            <ReportLibraryAccordion
                sectionId={section.id}
                title={section.title}
                count={section.reports.length}
                isOpen={isOpen}
                onToggle={onToggle}
                dragHandleProps={canReorder ? { ...attributes, ...listeners } : undefined}
                isSectionDragging={isDragging}
            >
                <ReportSectionItems
                    section={section}
                    selectedReportId={selectedReportId}
                    selectedViewId={selectedViewId}
                    onSelectReport={onSelectReport}
                    onToggleFavorite={onToggleFavorite}
                    canReorder={canReorder}
                />
            </ReportLibraryAccordion>
        </div>
    );
}

export function ReportLibrary({
    sections,
    selectedReportId,
    selectedViewId,
    myViews,
    sharedViews,
    onSelectReport,
    onSelectView,
    onToggleFavorite,
    onReorderSection,
    onReorderSections,
    isCollapsed,
    onToggleCollapse,
}: ReportLibraryProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedSectionIds, setExpandedSectionIds] = useState<Set<string>>(
        () => new Set(getStoredExpandedSectionIds()),
    );
    const [isListScrolled, setIsListScrolled] = useState(false);
    const [canScrollMore, setCanScrollMore] = useState(false);
    const [activeDrag, setActiveDrag] = useState<{
        type: 'report' | 'section';
        report?: ReportDefinition;
        section?: ReportSection;
        sectionId?: string;
        width?: number;
    } | null>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 6,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const query = searchQuery.trim().toLowerCase();

    const syncListOverflow = () => {
        const element = listRef.current;
        if (!element) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = element;
        setIsListScrolled(scrollTop > 0);
        setCanScrollMore(scrollHeight - clientHeight - scrollTop > 1);
    };

    const toggleSection = (sectionId: string) => {
        setExpandedSectionIds((current) => {
            const next = new Set(current);
            if (next.has(sectionId)) {
                next.delete(sectionId);
            } else {
                next.add(sectionId);
            }
            setStoredExpandedSectionIds(Array.from(next));
            return next;
        });
    };

    const isSectionOpen = (sectionId: string) =>
        Boolean(query) || expandedSectionIds.has(sectionId);

    const filteredSections = useMemo(() => {
        if (!query) {
            return sections;
        }

        return sections
            .map((section) => ({
                ...section,
                reports: section.reports.filter(
                    (report) =>
                        report.title.toLowerCase().includes(query) ||
                        report.category.toLowerCase().includes(query),
                ),
            }))
            .filter((section) => section.reports.length > 0);
    }, [sections, query]);

    const filteredMyViews = useMemo(() => {
        if (!query) {
            return myViews;
        }

        return myViews.filter(
            (view) =>
                view.name.toLowerCase().includes(query) ||
                view.baseReportId.toLowerCase().includes(query),
        );
    }, [myViews, query]);

    const filteredSharedViews = useMemo(() => {
        if (!query) {
            return sharedViews;
        }

        return sharedViews.filter(
            (view) =>
                view.name.toLowerCase().includes(query) ||
                view.baseReportId.toLowerCase().includes(query),
        );
    }, [sharedViews, query]);

    const hasAnyResults =
        filteredSections.length > 0 || filteredMyViews.length > 0 || filteredSharedViews.length > 0;
    const canReorder = !query;
    const favoriteSection = filteredSections.find((section) => section.id === FAVORITES_SECTION_ID);
    const movableSections = filteredSections.filter(
        (section) => section.id !== FAVORITES_SECTION_ID,
    );

    const handleCatalogDragStart = (event: DragStartEvent) => {
        const type = event.active.data.current?.type;
        const width = event.active.rect.current.initial?.width;

        if (type === 'report') {
            const sectionId = event.active.data.current?.sectionId as string;
            const reportId = event.active.data.current?.reportId as string;
            const section = filteredSections.find((item) => item.id === sectionId);
            const report = section?.reports.find((item) => item.id === reportId);
            if (report) {
                setActiveDrag({ type: 'report', report, sectionId, width });
            }
            return;
        }

        if (type === 'section') {
            const sectionId = event.active.data.current?.sectionId as string;
            const section = movableSections.find((item) => item.id === sectionId);
            if (section) {
                setActiveDrag({ type: 'section', section, width });
            }
        }
    };

    const handleCatalogDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        setActiveDrag(null);

        if (!over || active.id === over.id) {
            return;
        }

        const activeType = active.data.current?.type;
        const overType = over.data.current?.type;
        if (activeType !== overType) {
            return;
        }

        if (activeType === 'report') {
            const sectionId = active.data.current?.sectionId as string;
            if (over.data.current?.sectionId !== sectionId) {
                return;
            }

            const section = filteredSections.find((item) => item.id === sectionId);
            if (!section) {
                return;
            }

            const reportIds = section.reports.map((report) => report.id);
            const oldIndex = reportIds.indexOf(active.data.current?.reportId as string);
            const newIndex = reportIds.indexOf(over.data.current?.reportId as string);
            if (oldIndex === -1 || newIndex === -1) {
                return;
            }

            onReorderSection(sectionId, arrayMove(reportIds, oldIndex, newIndex));
            return;
        }

        if (activeType === 'section') {
            const sectionIds = movableSections.map((section) => section.id);
            const oldIndex = sectionIds.indexOf(active.data.current?.sectionId as string);
            const newIndex = sectionIds.indexOf(over.data.current?.sectionId as string);
            if (oldIndex === -1 || newIndex === -1) {
                return;
            }

            onReorderSections(arrayMove(sectionIds, oldIndex, newIndex));
        }
    };

    useLayoutEffect(() => {
        syncListOverflow();

        const element = listRef.current;
        if (!element) {
            return;
        }

        const observer = new ResizeObserver(() => {
            syncListOverflow();
        });
        observer.observe(element);
        if (element.firstElementChild) {
            observer.observe(element.firstElementChild);
        }

        return () => observer.disconnect();
    }, [expandedSectionIds, hasAnyResults, query, isCollapsed]);

    return (
        <div className="reports-library relative h-full w-full overflow-hidden rounded-none">
            <Tooltip message={isCollapsed ? 'Expand report library' : 'Collapse report library'}>
                <button
                    type="button"
                    onClick={onToggleCollapse}
                    aria-label={isCollapsed ? 'Expand report library' : 'Collapse report library'}
                    className={cn(
                        'z-10 transition-all duration-300 ease-in-out',
                        collapseButtonClass,
                        isCollapsed
                            ? 'absolute top-4 left-1/2 -translate-x-1/2'
                            : 'absolute right-5 top-5',
                    )}
                >
                    {isCollapsed ? (
                        <PanelLeftOpen className="h-4 w-4" />
                    ) : (
                        <PanelLeftClose className="h-4 w-4" />
                    )}
                </button>
            </Tooltip>

            <button
                type="button"
                onClick={onToggleCollapse}
                aria-label="Expand sidebar"
                title="Expand sidebar"
                disabled={!isCollapsed}
                tabIndex={isCollapsed ? 0 : -1}
                className={cn(
                    'absolute inset-0 z-0 flex items-center justify-center bg-card',
                    isCollapsed
                        ? 'cursor-pointer border-0 pointer-events-auto opacity-100 transition-opacity duration-150 ease-in-out hover:bg-card'
                        : 'pointer-events-none border-0 opacity-0 transition-none',
                )}
                style={isCollapsed ? { transitionDelay: `${SIDEBAR_TRANSITION_MS}ms` } : undefined}
                aria-hidden={!isCollapsed}
            >
                <span
                    className="text-sm font-semibold text-foreground [writing-mode:vertical-rl] rotate-180"
                    aria-hidden
                >
                    Report Library
                </span>
            </button>

            <div
                className={cn(
                    'absolute inset-0 flex w-[320px] flex-col p-4',
                    isCollapsed
                        ? 'pointer-events-none opacity-0 transition-none'
                        : 'opacity-100 transition-opacity duration-150 ease-in-out',
                )}
                style={!isCollapsed ? { transitionDelay: `${SIDEBAR_TRANSITION_MS}ms` } : undefined}
                aria-hidden={isCollapsed}
            >
                <div className="pr-8">
                    <h2 className="mt-0 text-base font-semibold text-foreground">Report Library</h2>
                </div>

                <div className="relative mt-4">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search reports..."
                        className="pl-9"
                    />
                </div>

                <Separator className="mb-2 mt-4" />

                <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                    {isListScrolled ? (
                        <div
                            className="reports-library-scroll-fade pointer-events-none absolute inset-x-0 top-0 z-10 h-5"
                            aria-hidden
                        />
                    ) : null}
                    <div
                        ref={listRef}
                        className="reports-library-scroll min-h-0 flex-1 overflow-y-auto"
                        onScroll={syncListOverflow}
                    >
                        <div className="flex flex-col gap-5">
                            {!hasAnyResults ? (
                                <p className="text-sm text-muted-foreground">No reports found.</p>
                            ) : (
                                <>
                                    {filteredMyViews.length > 0 ? (
                                        <ReportLibraryAccordion
                                            sectionId={MY_VIEWS_SECTION_ID}
                                            title="My views"
                                            count={filteredMyViews.length}
                                            isOpen={isSectionOpen(MY_VIEWS_SECTION_ID)}
                                            onToggle={() => toggleSection(MY_VIEWS_SECTION_ID)}
                                        >
                                            {filteredMyViews.map((view) => (
                                                <ReportLibraryItem
                                                    key={`my-view-${view.eventReportViewId}`}
                                                    report={{
                                                        id: String(view.eventReportViewId),
                                                        title: view.name,
                                                        description: view.description ?? '',
                                                        category: 'Online Sales',
                                                        hasTable: true,
                                                        sharedWithTeam: view.visibility === 'team',
                                                    }}
                                                    isActive={
                                                        selectedViewId === view.eventReportViewId
                                                    }
                                                    showCategoryBadge={false}
                                                    onSelect={() => onSelectView(view)}
                                                    onToggleFavorite={() => undefined}
                                                    leadingIcon="custom"
                                                />
                                            ))}
                                        </ReportLibraryAccordion>
                                    ) : null}

                                    {filteredSharedViews.length > 0 ? (
                                        <ReportLibraryAccordion
                                            sectionId={SHARED_VIEWS_SECTION_ID}
                                            title="Shared with team"
                                            count={filteredSharedViews.length}
                                            isOpen={isSectionOpen(SHARED_VIEWS_SECTION_ID)}
                                            onToggle={() => toggleSection(SHARED_VIEWS_SECTION_ID)}
                                        >
                                            {filteredSharedViews.map((view) => (
                                                <ReportLibraryItem
                                                    key={`shared-view-${view.eventReportViewId}`}
                                                    report={{
                                                        id: String(view.eventReportViewId),
                                                        title: view.name,
                                                        description: view.description ?? '',
                                                        category: 'Online Sales',
                                                        hasTable: true,
                                                        sharedWithTeam: true,
                                                    }}
                                                    isActive={
                                                        selectedViewId === view.eventReportViewId
                                                    }
                                                    showCategoryBadge={false}
                                                    onSelect={() => onSelectView(view)}
                                                    onToggleFavorite={() => undefined}
                                                    leadingIcon="shared"
                                                />
                                            ))}
                                        </ReportLibraryAccordion>
                                    ) : null}

                                    {canReorder ? (
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestSameType}
                                            modifiers={[restrictToVerticalAxis]}
                                            onDragStart={handleCatalogDragStart}
                                            onDragEnd={handleCatalogDragEnd}
                                            onDragCancel={() => setActiveDrag(null)}
                                        >
                                            <div className="flex flex-col gap-5">
                                                {favoriteSection ? (
                                                    <ReportLibraryAccordion
                                                        sectionId={favoriteSection.id}
                                                        title={favoriteSection.title}
                                                        count={favoriteSection.reports.length}
                                                        isOpen={isSectionOpen(favoriteSection.id)}
                                                        onToggle={() =>
                                                            toggleSection(favoriteSection.id)
                                                        }
                                                    >
                                                        <ReportSectionItems
                                                            section={favoriteSection}
                                                            selectedReportId={selectedReportId}
                                                            selectedViewId={selectedViewId}
                                                            onSelectReport={onSelectReport}
                                                            onToggleFavorite={onToggleFavorite}
                                                            canReorder
                                                        />
                                                    </ReportLibraryAccordion>
                                                ) : null}
                                                <SortableContext
                                                    items={movableSections.map((section) =>
                                                        getSortableSectionId(section.id),
                                                    )}
                                                    strategy={verticalListSortingStrategy}
                                                >
                                                    {movableSections.map((section) => (
                                                        <SortableLibrarySection
                                                            key={section.id}
                                                            section={section}
                                                            selectedReportId={selectedReportId}
                                                            selectedViewId={selectedViewId}
                                                            onSelectReport={onSelectReport}
                                                            onToggleFavorite={onToggleFavorite}
                                                            canReorder
                                                            isOpen={isSectionOpen(section.id)}
                                                            onToggle={() =>
                                                                toggleSection(section.id)
                                                            }
                                                        />
                                                    ))}
                                                </SortableContext>
                                            </div>
                                            <DragOverlay zIndex={80}>
                                                {activeDrag?.type === 'report' &&
                                                activeDrag.report ? (
                                                    <div
                                                        className="reports-light-overlay"
                                                        style={{ width: activeDrag.width }}
                                                    >
                                                        <ReportLibraryItem
                                                            report={activeDrag.report}
                                                            isActive={
                                                                !selectedViewId &&
                                                                selectedReportId ===
                                                                    activeDrag.report.id
                                                            }
                                                            showCategoryBadge={
                                                                activeDrag.sectionId ===
                                                                FAVORITES_SECTION_ID
                                                            }
                                                            onSelect={onSelectReport}
                                                            onToggleFavorite={onToggleFavorite}
                                                            dragHandleProps={{}}
                                                            isDragging
                                                        />
                                                    </div>
                                                ) : null}
                                                {activeDrag?.type === 'section' &&
                                                activeDrag.section ? (
                                                    <div
                                                        className="reports-light-overlay"
                                                        style={{ width: activeDrag.width }}
                                                    >
                                                        <ReportLibrarySectionOverlay
                                                            sectionId={activeDrag.section.id}
                                                            title={activeDrag.section.title}
                                                            count={
                                                                activeDrag.section.reports.length
                                                            }
                                                        />
                                                    </div>
                                                ) : null}
                                            </DragOverlay>
                                        </DndContext>
                                    ) : (
                                        filteredSections.map((section) => (
                                            <ReportLibraryAccordion
                                                key={section.id}
                                                sectionId={section.id}
                                                title={section.title}
                                                count={section.reports.length}
                                                isOpen={isSectionOpen(section.id)}
                                                onToggle={() => toggleSection(section.id)}
                                            >
                                                <ReportSectionItems
                                                    section={section}
                                                    selectedReportId={selectedReportId}
                                                    selectedViewId={selectedViewId}
                                                    onSelectReport={onSelectReport}
                                                    onToggleFavorite={onToggleFavorite}
                                                    canReorder={false}
                                                />
                                            </ReportLibraryAccordion>
                                        ))
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    {hasAnyResults && canScrollMore ? (
                        <div
                            className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex h-14 items-center justify-center"
                            aria-hidden
                        >
                            <div className="reports-library-scroll-fade-bottom absolute inset-0" />
                            <p className="relative z-10 flex items-center gap-1 bg-card px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                <ArrowDown className="h-3.5 w-3.5" />
                                Scroll to see more reports
                            </p>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
